import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import {
    formatRateForInput,
    type Service,
    useAddServiceOptions,
    useUpdateServiceOptions,
} from '@/entities/service-catalog';

import {
    type SaveServiceFormOutput,
    type SaveServiceFormValues,
    saveServiceFormSchema,
} from './save-service-form.schema';

type Options = {
    // Presence of `service` selects edit mode, the way `useSaveLocationForm` branches on `location`.
    service?: Service;
    onSuccess: (code: string) => void;
};

export function useSaveServiceForm({ service, onSuccess }: Options) {
    const addService = useMutation(useAddServiceOptions());
    const updateService = useMutation(useUpdateServiceOptions());
    const isEdit = service !== undefined;

    const form = useForm<SaveServiceFormValues, unknown, SaveServiceFormOutput>({
        resolver: zodResolver(saveServiceFormSchema),
        defaultValues: service
            ? {
                  code: service.code,
                  description: service.description,
                  makeDefault: service.isDefault,
                  rate: formatRateForInput(service.rateCents),
                  duration: String(service.durationMinutes),
              }
            : {
                  code: '',
                  description: '',
                  makeDefault: false,
                  rate: '',
                  duration: '',
              },
    });

    const onSubmit = form.handleSubmit((data) => {
        if (isEdit) {
            updateService.mutate(data, {
                onSuccess: () => {
                    toast.success('Service updated');
                    onSuccess(data.code);
                },
            });
            return;
        }

        addService.mutate(data, {
            onSuccess: () => {
                toast.success('Service added');
                onSuccess(data.code);
            },
        });
    });

    // Read at render: formState's proxy only tracks a key a render has already read.
    const { isSubmitted } = form.formState;

    // The picker reports a listed code's own description, or '' for a custom code — both
    // replace whatever Description holds, same as `setOffersTelehealth` replaces a stored
    // choice in `useSaveLocationForm`.
    // Before a submit, validating would flag the Description a custom code just emptied.
    const selectCode = (code: string, description: string) => {
        form.setValue('code', code, { shouldValidate: isSubmitted });
        form.setValue('description', description, { shouldValidate: isSubmitted });
    };

    return {
        form,
        onSubmit,
        selectCode,
        isPending: addService.isPending || updateService.isPending,
        isEdit,
    };
}
